import React, { createContext, useContext, useEffect, useState } from "react";

export type LangCode = "en" | "ru" | "tr" | "de" | "fr" | "nl" | "es" | "it" | "hr" | "ro" | "pt" | "pl";
export type ThemeMode = "light" | "dark";

export interface LangInfo {
  code: LangCode;
  label: string;
  flag: string;
  nativeName: string;
}

export const LANGUAGES: LangInfo[] = [
  { code: "en", label: "EN", flag: "🇬🇧", nativeName: "English" },
  { code: "tr", label: "TR", flag: "🇹🇷", nativeName: "Türkçe" },
  { code: "de", label: "DE", flag: "🇩🇪", nativeName: "Deutsch" },
  { code: "fr", label: "FR", flag: "🇫🇷", nativeName: "Français" },
  { code: "nl", label: "NL", flag: "🇳🇱", nativeName: "Nederlands" },
  { code: "es", label: "ES", flag: "🇪🇸", nativeName: "Español" },
  { code: "it", label: "IT", flag: "🇮🇹", nativeName: "Italiano" },
  { code: "hr", label: "HR", flag: "🇭🇷", nativeName: "Hrvatski" },
  { code: "ro", label: "RO", flag: "🇷🇴", nativeName: "Română" },
  { code: "ru", label: "RU", flag: "🇷🇺", nativeName: "Русский" },
  { code: "pt", label: "PT", flag: "🇵🇹", nativeName: "Português" },
  { code: "pl", label: "PL", flag: "🇵🇱", nativeName: "Polski" },
];

export interface UiStrings {
  navProducts: string;
  navSolutions: string;
  navTools: string;
  navGuides: string;
  navSupport: string;
  addFile: string;
  addFolder: string;
  freeTag: string;
  noSignupTag: string;
  upTo5GbTag: string;
  increaseLimit: string;
  send1Tb: string;
  limeDropP2p: string;
  recipientEmailPlaceholder: string;
  transferTitlePlaceholder: string;
  messagePlaceholder: string;
  passwordPlaceholder: string;
  sendBtn: string;
  burnAfterDownload: string;
  exp1h: string;
  exp6h: string;
  exp24h: string;
  exp72h: string;
  exp7d: string;
  exp30d: string;
  exp1y: string;
  tabTeams: string;
  tabExtra: string;
  tabExceed: string;
  tabExtend: string;
  perUserPricing: string;
  onePriceArrow: string;
  learnMore: string;
  heroTeamsTitle: string;
  heroTeamsDesc: string;
  heroTeamsPrice: string;
  heroTeamsCta: string;
  heroExtraTitle: string;
  heroExtraAccent: string;
  heroExtraDesc: string;
  heroExtraCta: string;
  heroExceedTitle: string;
  heroExceedAccent: string;
  heroExceedDesc: string;
  heroExceedCta: string;
  heroExtendTitle: string;
  heroExtendAccent: string;
  heroExtendDesc: string;
  heroExtendCta: string;
  whatIsTitle: string;
  whatIsSub: string;
  feat1Title: string;
  feat1Desc: string;
  feat2Title: string;
  feat2Desc: string;
  feat3Title: string;
  feat3Desc: string;
  guidesTitle: string;
  guidesSub: string;
  seeAllGuides: string;
  pricingTitle: string;
  pricingSub: string;
  individual: string;
  teams: string;
  annual: string;
  twoYears: string;
  getStarted: string;
  trustedTitle: string;
  trustedSub: string;
  faqTitle: string;
  compareTitle: string;
  compareSub: string;
  ctaReadyTitle: string;
  ctaReadySub: string;
  ctaStartBtn: string;
  techSpecsTitle: string;
  stepByStepTitle: string;
}

const EN: UiStrings = {
  navProducts: "Products",
  navSolutions: "Solutions",
  navTools: "Tools",
  navGuides: "Guides",
  navSupport: "Support",
  addFile: "Add File",
  addFolder: "Add Folder",
  freeTag: "Free",
  noSignupTag: "No sign-up",
  upTo5GbTag: "Up to 5 GB",
  increaseLimit: "Increase Limit ⚡",
  send1Tb: "Send 1 TB",
  limeDropP2p: "LimeDrop P2P",
  recipientEmailPlaceholder: "Recipient email or contact note (optional)",
  transferTitlePlaceholder: "Transfer name / project title (optional)",
  messagePlaceholder: "Write your encrypted message (optional)",
  passwordPlaceholder: "Password (optional)",
  sendBtn: "ENCRYPT & SEND",
  burnAfterDownload: "Burn after 1st download",
  exp1h: "1 hour",
  exp6h: "6 hours",
  exp24h: "24 hours",
  exp72h: "72 hours (3 days)",
  exp7d: "7 days",
  exp30d: "30 days (≤100 MB)",
  exp1y: "1 Year (Extend)",
  tabTeams: "Teams",
  tabExtra: "Extra",
  tabExceed: "Exceed",
  tabExtend: "Extend",
  perUserPricing: "per-user pricing",
  onePriceArrow: "→ one price",
  learnMore: "Learn more",
  heroTeamsTitle: "25 people. One price.",
  heroTeamsDesc: "No per-seat fees, ever. Your whole team shares 2 TB of zero-knowledge encrypted transfer storage on one yearly plan. Add a colleague, not a cost.",
  heroTeamsPrice: "$149.00/year for the whole team",
  heroTeamsCta: "See Teams Plan",
  heroExtraTitle: "1 TB Zero-Knowledge Storage.",
  heroExtraAccent: "$49/year.",
  heroExtraDesc: "Send up to 500 GB per transfer, keep files online for up to 365 days, and protect every client delivery with browser-side AES-256-GCM encryption.",
  heroExtraCta: "Get Extra 1000 ($49/yr)",
  heroExceedTitle: "Send up to 1 TB",
  heroExceedAccent: "without signing up.",
  heroExceedDesc: "Do not waste time becoming a member. Send 50 GB ($2.49), 200 GB ($4.49), or 1 TB ($8.99) in a single encrypted transfer with zero registration.",
  heroExceedCta: "Send 50 GB – 1 TB Now",
  heroExtendTitle: "Keep files alive for",
  heroExtendAccent: "1 full year.",
  heroExtendDesc: "Extend any transfer up to 5 GB for 6 months ($1.99) or 1 year ($2.99) with unlimited downloads. Both senders and recipients can freeze the expiration timer.",
  heroExtendCta: "Extend Storage ($1.99)",
  whatIsTitle: "What is LimeShare?",
  whatIsSub: "LimeShare is a zero-knowledge file transfer service that lets you send large files up to 5 GB by link or P2P, without registration.",
  feat1Title: "Free Transfer (No Signup)",
  feat1Desc: "Always Free. No Signup Needed. Send files and folders up to 5 GB for free, anytime.",
  feat2Title: "True Zero-Knowledge E2EE",
  feat2Desc: "Encrypted inside your browser with AES-256-GCM before upload. Not even our servers can see your files.",
  feat3Title: "Fast Global Edge & P2P",
  feat3Desc: "Upload and download at unthrottled gigabit speeds worldwide, or stream device-to-device via LimeDrop P2P.",
  guidesTitle: "A guide for every file and every app",
  guidesSub: "From PDF and ZIP to Discord and WhatsApp: pick what you are sending.",
  seeAllGuides: "See all guides",
  pricingTitle: "LimeShare Extra & Teams",
  pricingSub: "Trusted by tens of thousands of professionals worldwide. Discover the plan that fits you best.",
  individual: "Individual",
  teams: "Teams (25 Seats)",
  annual: "Annual",
  twoYears: "2 Years (-20%)",
  getStarted: "Get Started",
  trustedTitle: "Trusted by Professionals Worldwide",
  trustedSub: "See why creators, engineers, and legal teams choose LimeShare for confidential file transfer.",
  faqTitle: "Frequently Asked Questions",
  compareTitle: "The Best WeTransfer & SendGB Alternative: Why Choose LimeShare?",
  compareSub: "Free Transfer Limit & Privacy Comparison",
  ctaReadyTitle: "Send up to 5 GB free right now",
  ctaReadySub: "Up to 500 files or folders · Client-side AES-256-GCM encryption · Zero accounts required",
  ctaStartBtn: "Start Free Transfer",
  techSpecsTitle: "Technical specifications",
  stepByStepTitle: "Step-by-step guide",
};

const TRANSLATIONS: Record<LangCode, Partial<UiStrings>> = {
  en: EN,
  ru: {
    navProducts: "Продукты",
    navSolutions: "Решения",
    navTools: "Инструменты",
    navGuides: "Гайды",
    navSupport: "Поддержка",
    addFile: "Добавить файл",
    addFolder: "Добавить папку",
    freeTag: "Бесплатно",
    noSignupTag: "Без регистрации",
    upTo5GbTag: "До 5 ГБ",
    increaseLimit: "Увеличить лимит ⚡",
    send1Tb: "Отправить 1 ТБ",
    limeDropP2p: "LimeDrop P2P",
    recipientEmailPlaceholder: "Email получателя или заметка (опционально)",
    transferTitlePlaceholder: "Название проекта / передачи (опционально)",
    messagePlaceholder: "Зашифрованное сообщение получателю (опционально)",
    passwordPlaceholder: "Пароль (опционально)",
    sendBtn: "ЗАШИФРОВАТЬ И ОТПРАВИТЬ",
    burnAfterDownload: "Сжечь после 1-го скачивания",
    exp1h: "1 час",
    exp6h: "6 часов",
    exp24h: "24 часа",
    exp72h: "72 часа (3 дня)",
    exp7d: "7 дней",
    exp30d: "30 дней (≤100 МБ)",
    exp1y: "1 Год (Extend)",
    tabTeams: "Команды",
    tabExtra: "Extra",
    tabExceed: "Exceed 1 ТБ",
    tabExtend: "Продление",
    perUserPricing: "оплата за место",
    onePriceArrow: "→ единая цена",
    learnMore: "Подробнее",
    heroTeamsTitle: "25 человек. Одна цена.",
    heroTeamsDesc: "Никакой оплаты за каждого сотрудника. Вся команда делит 2 ТБ защищённого Zero-Knowledge хранилища по одной годовой цене.",
    heroTeamsPrice: "$149.00/год за всю команду",
    heroTeamsCta: "Тариф для команд",
    heroExtraTitle: "1 ТБ Zero-Knowledge хранилища.",
    heroExtraAccent: "$49 в год.",
    heroExtraDesc: "Отправляйте до 500 ГБ за раз, храните файлы до 365 дней и защищайте каждую передачу клиентским шифрованием AES-256-GCM в браузере.",
    heroExtraCta: "Подключить Extra 1000 ($49/год)",
    heroExceedTitle: "Отправляйте до 1 ТБ",
    heroExceedAccent: "без регистрации.",
    heroExceedDesc: "Не нужно покупать подписку ради одного большого проекта. Отправьте 50 ГБ ($2.49), 200 ГБ ($4.49) или 1 ТБ ($8.99) разовым платежом без создания аккаунта.",
    heroExceedCta: "Отправить 50 ГБ – 1 ТБ",
    heroExtendTitle: "Храните файлы онлайн",
    heroExtendAccent: "целый 1 год.",
    heroExtendDesc: "Продлите жизнь любой ссылки до 5 ГБ на 6 месяцев ($1.99) или 1 год ($2.99). Заморозить таймер удаления может как отправитель, так и получатель.",
    heroExtendCta: "Продлить хранение ($1.99)",
    whatIsTitle: "Что такое LimeShare?",
    whatIsSub: "LimeShare — сервис передачи больших файлов до 5 ГБ бесплатно без регистрации с шифрованием прямо в браузере.",
    feat1Title: "Бесплатно и без аккаунта",
    feat1Desc: "Отправляйте файлы и целые папки до 5 ГБ бесплатно в любое время без регистрации.",
    feat2Title: "Zero-Knowledge шифрование",
    feat2Desc: "Шифрование AES-256-GCM в браузере до отправки. Даже наши серверы не видят ваши файлы и их имена.",
    feat3Title: "Максимальная скорость + P2P",
    feat3Desc: "Гигабитная скорость без ограничений по всему миру или прямая передача между устройствами через LimeDrop.",
    guidesTitle: "Гайд для каждого файла и приложения",
    guidesSub: "От PDF и ZIP до Discord, Telegram и WhatsApp: выберите ваш сценарий.",
    seeAllGuides: "Все руководства",
    pricingTitle: "LimeShare Extra и Teams",
    pricingSub: "Выбор десятков тысяч профессионалов по всему миру. Подберите оптимальный тариф.",
    individual: "Индивидуальный",
    teams: "Команда (25 мест)",
    annual: "1 Год",
    twoYears: "2 Года (-20%)",
    getStarted: "Подключить",
    trustedTitle: "Нам доверяют профессионалы по всему миру",
    trustedSub: "Узнайте, почему видеомонтажёры, юристы и разработчики выбирают LimeShare.",
    faqTitle: "Часто задаваемые вопросы",
    compareTitle: "Лучшая альтернатива WeTransfer и SendGB: почему LimeShare?",
    compareSub: "Сравнение бесплатных лимитов и реальной приватности",
    ctaReadyTitle: "Отправьте до 5 ГБ бесплатно прямо сейчас",
    ctaReadySub: "До 500 файлов или целая папка · Шифрование AES-256-GCM в браузере · Без регистрации",
    ctaStartBtn: "Начать бесплатную отправку",
    techSpecsTitle: "Технические характеристики",
    stepByStepTitle: "Пошаговая инструкция",
  },
  de: {
    navProducts: "Produkte",
    navSolutions: "Lösungen",
    navTools: "Tools",
    navSupport: "Support",
    addFile: "Datei wählen",
    addFolder: "Ordner wählen",
    freeTag: "Kostenlos",
    noSignupTag: "Ohne Anmeldung",
    upTo5GbTag: "Bis zu 5 GB",
    increaseLimit: "Limit erhöhen ⚡",
    send1Tb: "1 TB senden",
    sendBtn: "VERSCHLÜSSELN & SENDEN",
    burnAfterDownload: "Nach 1. Download löschen",
    exp1h: "1 Stunde",
    exp6h: "6 Stunden",
    exp24h: "24 Stunden",
    exp72h: "72 Stunden (3 Tage)",
    exp7d: "7 Tage",
    exp30d: "30 Tage (≤100 MB)",
    exp1y: "1 Jahr (Extend)",
    heroTeamsTitle: "25 Personen. Ein Preis.",
    heroTeamsDesc: "Keine Gebühren pro Nutzer. Ihr gesamtes Team teilt sich 2 TB Zero-Knowledge-Speicher zu einem Jahrespreis.",
    heroTeamsPrice: "149,00 $/Jahr für das gesamte Team",
    heroTeamsCta: "Team-Plan ansehen",
    heroExtraTitle: "1 TB Zero-Knowledge-Speicher.",
    heroExtraAccent: "49 $/Jahr.",
    heroExtraDesc: "Senden Sie bis zu 500 GB pro Transfer und speichern Sie Dateien bis zu 365 Tage mit Browser-Verschlüsselung.",
    heroExtraCta: "Extra 1000 sichern (49 $/J.)",
    heroExceedTitle: "Bis zu 1 TB senden",
    heroExceedAccent: "ohne Registrierung.",
    heroExceedDesc: "Einmalig 50 GB (2,49 $), 200 GB (4,49 $) oder 1 TB (8,99 $) ohne Abo versenden.",
    heroExceedCta: "50 GB – 1 TB senden",
    heroExtendTitle: "Dateien verfügbar halten für",
    heroExtendAccent: "1 ganzes Jahr.",
    heroExtendDesc: "Verlängern Sie jeden Transfer bis 5 GB auf 6 Monate (1,99 $) oder 1 Jahr (2,99 $).",
    heroExtendCta: "Speicherzeit verlängern (1,99 $)",
    whatIsTitle: "Was ist LimeShare?",
    whatIsSub: "LimeShare ist ein Zero-Knowledge-Dateitransferdienst, mit dem Sie Dateien bis 5 GB kostenlos ohne Registrierung senden.",
    ctaReadyTitle: "Jetzt bis zu 5 GB kostenlos senden",
    ctaStartBtn: "Kostenlosen Transfer starten",
  },
  fr: {
    navProducts: "Produits",
    navSolutions: "Solutions",
    navTools: "Outils",
    navSupport: "Support",
    addFile: "Ajouter fichier",
    addFolder: "Ajouter dossier",
    freeTag: "Gratuit",
    noSignupTag: "Sans inscription",
    upTo5GbTag: "Jusqu'à 5 Go",
    increaseLimit: "Augmenter la limite ⚡",
    send1Tb: "Envoyer 1 To",
    sendBtn: "CHIFFRER & ENVOYER",
    burnAfterDownload: "Détruire après 1er téléchargement",
    heroTeamsTitle: "25 personnes. Un seul prix.",
    heroTeamsDesc: "Aucun frais par utilisateur. Toute votre équipe partage 2 To de stockage chiffré Zero-Knowledge.",
    heroTeamsPrice: "149,00 $/an pour toute l'équipe",
    heroTeamsCta: "Voir l'offre Équipe",
    heroExtraTitle: "1 To de stockage Zero-Knowledge.",
    heroExtraAccent: "49 $/an.",
    heroExtraDesc: "Envoyez jusqu'à 500 Go par transfert et conservez vos fichiers 365 jours avec chiffrement dans le navigateur.",
    heroExtraCta: "Choisir Extra 1000 (49 $/an)",
    heroExceedTitle: "Envoyez jusqu'à 1 To",
    heroExceedAccent: "sans inscription.",
    heroExceedDesc: "Envoyez 50 Go (2,49 $), 200 Go (4,49 $) ou 1 To (8,99 $) en une fois sans abonnement.",
    heroExceedCta: "Envoyer 50 Go – 1 To",
    heroExtendTitle: "Gardez vos fichiers actifs",
    heroExtendAccent: "pendant 1 an.",
    heroExtendDesc: "Prolongez n'importe quel transfert jusqu'à 5 Go pour 6 mois (1,99 $) ou 1 an (2,99 $).",
    heroExtendCta: "Prolonger (1,99 $)",
    whatIsTitle: "Qu'est-ce que LimeShare ?",
    ctaReadyTitle: "Envoyez jusqu'à 5 Go gratuitement",
    ctaStartBtn: "Démarrer le transfert gratuit",
  },
  es: {
    navProducts: "Productos",
    navSolutions: "Soluciones",
    navTools: "Herramientas",
    navSupport: "Soporte",
    addFile: "Añadir archivo",
    addFolder: "Añadir carpeta",
    freeTag: "Gratis",
    noSignupTag: "Sin registro",
    upTo5GbTag: "Hasta 5 GB",
    increaseLimit: "Aumentar límite ⚡",
    send1Tb: "Enviar 1 TB",
    sendBtn: "CIFRAR Y ENVIAR",
    burnAfterDownload: "Quemar tras 1ª descarga",
    heroTeamsTitle: "25 personas. Un solo precio.",
    heroTeamsDesc: "Sin cargos por usuario. Todo tu equipo comparte 2 TB de almacenamiento cifrado Zero-Knowledge.",
    heroTeamsPrice: "$149.00/año para todo el equipo",
    heroTeamsCta: "Ver plan Equipos",
    heroExtraTitle: "1 TB de almacenamiento Zero-Knowledge.",
    heroExtraAccent: "$49/año.",
    heroExtraDesc: "Envía hasta 500 GB por transferencia y guarda archivos hasta 365 días con cifrado en el navegador.",
    heroExtraCta: "Obtener Extra 1000 ($49/año)",
    heroExceedTitle: "Envía hasta 1 TB",
    heroExceedAccent: "sin registrarte.",
    heroExceedDesc: "Envía 50 GB ($2.49), 200 GB ($4.49) o 1 TB ($8.99) en un pago único sin suscripción.",
    heroExceedCta: "Enviar 50 GB – 1 TB",
    heroExtendTitle: "Mantén tus archivos activos",
    heroExtendAccent: "durante 1 año.",
    heroExtendDesc: "Extiende cualquier transferencia hasta 5 GB por 6 meses ($1.99) o 1 año ($2.99).",
    heroExtendCta: "Extender ($1.99)",
    whatIsTitle: "¿Qué es LimeShare?",
    ctaReadyTitle: "Envía hasta 5 GB gratis ahora",
    ctaStartBtn: "Iniciar transferencia gratis",
  },
  tr: {
    navProducts: "Ürünler",
    navSolutions: "Çözümler",
    navTools: "Araçlar",
    navSupport: "Destek",
    addFile: "Dosya Ekle",
    addFolder: "Klasör Ekle",
    freeTag: "Ücretsiz",
    noSignupTag: "Üyeliksiz",
    upTo5GbTag: "5 GB'a Kadar",
    increaseLimit: "Limiti Artır ⚡",
    send1Tb: "1 TB Gönder",
    sendBtn: "ŞİFRELE VE GÖNDER",
    burnAfterDownload: "İlk indirmede imha et",
    heroTeamsTitle: "25 kişi. Tek fiyat.",
    heroTeamsDesc: "Kullanıcı başı ücret yok. Tüm ekibiniz tek yıllık planda 2 TB şifreli alanı paylaşır.",
    heroTeamsPrice: "Tüm ekip için yılda $149.00",
    heroTeamsCta: "Ekip Planını Gör",
    heroExtraTitle: "1 TB Sıfır-Bilgi Depolama.",
    heroExtraAccent: "Yılda $49.",
    heroExtraDesc: "Transfer başına 500 GB'a kadar gönderin ve dosyaları 365 gün boyunca tarayıcı şifrelemesiyle saklayın.",
    heroExtraCta: "Extra 1000 Al ($49/yıl)",
    heroExceedTitle: "Üye olmadan",
    heroExceedAccent: "1 TB'a kadar gönderin.",
    heroExceedDesc: "Tek seferlik ödemeyle abonelik olmadan 50 GB ($2.49), 200 GB ($4.49) veya 1 TB ($8.99) gönderin.",
    heroExceedCta: "50 GB – 1 TB Gönder",
    heroExtendTitle: "Dosyalarınızı",
    heroExtendAccent: "1 yıl saklayın.",
    heroExtendDesc: "5 GB'a kadar transferlerinizi 6 ay ($1.99) veya 1 yıl ($2.99) boyunca aktif tutun.",
    heroExtendCta: "Süreyi Uzat ($1.99)",
    whatIsTitle: "LimeShare Nedir?",
    ctaReadyTitle: "Hemen ücretsiz 5 GB'a kadar gönderin",
    ctaStartBtn: "Ücretsiz Transfer Başlat",
  },
  nl: {
    addFile: "Bestand kiezen",
    addFolder: "Map kiezen",
    freeTag: "Gratis",
    noSignupTag: "Geen registratie",
    upTo5GbTag: "Tot 5 GB",
    sendBtn: "VERSLEUTEL & VERSTUUR",
    heroTeamsTitle: "25 mensen. Één prijs.",
    heroExtraTitle: "1 TB Zero-Knowledge Opslag.",
    heroExceedTitle: "Verstuur tot 1 TB",
    heroExtendTitle: "Bewaar bestanden",
  },
  it: {
    addFile: "Aggiungi file",
    addFolder: "Aggiungi cartella",
    freeTag: "Gratis",
    noSignupTag: "Senza registrazione",
    upTo5GbTag: "Fino a 5 GB",
    sendBtn: "CRITTOGRAFA E INVIA",
    heroTeamsTitle: "25 persone. Un solo prezzo.",
    heroExtraTitle: "1 TB di spazio Zero-Knowledge.",
    heroExceedTitle: "Invia fino a 1 TB",
    heroExtendTitle: "Mantieni i file attivi per",
  },
  pt: {
    addFile: "Adicionar ficheiro",
    addFolder: "Adicionar pasta",
    freeTag: "Grátis",
    noSignupTag: "Sem registo",
    upTo5GbTag: "Até 5 GB",
    sendBtn: "ENCRIPTAR E ENVIAR",
    heroTeamsTitle: "25 pessoas. Um preço único.",
    heroExtraTitle: "1 TB de armazenamento Zero-Knowledge.",
    heroExceedTitle: "Envie até 1 TB",
    heroExtendTitle: "Guarde ficheiros por",
  },
  pl: {
    addFile: "Dodaj plik",
    addFolder: "Dodaj folder",
    freeTag: "Za darmo",
    noSignupTag: "Bez rejestracji",
    upTo5GbTag: "Do 5 GB",
    sendBtn: "ZASZYFRUJ I WYŚLIJ",
    heroTeamsTitle: "25 osób. Jedna cena.",
    heroExtraTitle: "1 TB pamięci Zero-Knowledge.",
    heroExceedTitle: "Wyślij do 1 TB",
    heroExtendTitle: "Przechowuj pliki przez",
  },
  hr: {
    addFile: "Dodaj datoteku",
    addFolder: "Dodaj mapu",
    freeTag: "Besplatno",
    noSignupTag: "Bez registracije",
    upTo5GbTag: "Do 5 GB",
    sendBtn: "ŠIFRIRAJ I POŠALJI",
    heroTeamsTitle: "25 ljudi. Jedna cijena.",
  },
  ro: {
    addFile: "Adaugă fișier",
    addFolder: "Adaugă dosar",
    freeTag: "Gratuit",
    noSignupTag: "Fără înregistrare",
    upTo5GbTag: "Până la 5 GB",
    sendBtn: "CRIPTEAZĂ ȘI TRIMITE",
    heroTeamsTitle: "25 de persoane. Un singur preț.",
  },
};

interface UiContextValue {
  lang: LangCode;
  setLang: (l: LangCode) => void;
  t: UiStrings;
  theme: ThemeMode;
  setTheme: (m: ThemeMode) => void;
  toggleTheme: () => void;
}

const UiContext = createContext<UiContextValue>({
  lang: "en",
  setLang: () => undefined,
  t: EN,
  theme: "light",
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

export function detectLangFromPath(pathname: string): { lang: LangCode | null; strippedPath: string } {
  const segs = pathname.split("/").filter(Boolean);
  const first = segs[0]?.toLowerCase() as LangCode | undefined;
  if (first && first !== "en" && LANGUAGES.some((l) => l.code === first)) {
    const rest = "/" + segs.slice(1).join("/");
    return { lang: first, strippedPath: rest === "/" ? "/" : rest };
  }
  if (first === "en") {
    const rest = "/" + segs.slice(1).join("/");
    return { lang: "en", strippedPath: rest === "/" ? "/" : rest };
  }
  return { lang: null, strippedPath: pathname || "/" };
}

export function buildLangPath(strippedPath: string, targetLang: LangCode): string {
  const clean = strippedPath.startsWith("/") ? strippedPath : `/${strippedPath}`;
  if (targetLang === "en") return clean;
  return clean === "/" ? `/${targetLang}` : `/${targetLang}${clean}`;
}

export function UiProvider({ children, initialLang }: { children: React.ReactNode; initialLang?: LangCode }) {
  const [lang, setLangState] = useState<LangCode>(() => {
    if (initialLang) return initialLang;
    if (typeof window !== "undefined") {
      const fromUrl = detectLangFromPath(window.location.pathname).lang;
      if (fromUrl) return fromUrl;
    }
    return "en";
  });
  const [theme, setThemeState] = useState<ThemeMode>("light");

  useEffect(() => {
    try {
      const fromUrl = detectLangFromPath(window.location.pathname).lang;
      if (fromUrl) {
        setLangState(fromUrl);
        document.documentElement.lang = fromUrl;
      } else {
        const savedLang = localStorage.getItem("limeshare.lang") as LangCode | null;
        if (savedLang && LANGUAGES.some((x) => x.code === savedLang)) {
          setLangState(savedLang);
        }
      }
      const savedTheme = localStorage.getItem("limeshare.theme") as ThemeMode | null;
      const initialTheme: ThemeMode = savedTheme === "dark" || savedTheme === "light" ? savedTheme : "light";
      setThemeState(initialTheme);
      document.documentElement.setAttribute("data-theme", initialTheme);
    } catch {
      /* ignore */
    }
  }, []);

  const setLang = (l: LangCode) => {
    setLangState(l);
    try {
      localStorage.setItem("limeshare.lang", l);
      document.documentElement.lang = l;
    } catch {
      /* ignore */
    }
  };

  const setTheme = (m: ThemeMode) => {
    setThemeState(m);
    try {
      localStorage.setItem("limeshare.theme", m);
      document.documentElement.setAttribute("data-theme", m);
    } catch {
      /* ignore */
    }
  };

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const t: UiStrings = { ...EN, ...(TRANSLATIONS[lang] ?? {}) };

  return (
    <UiContext.Provider value={{ lang, setLang, t, theme, setTheme, toggleTheme }}>
      {children}
    </UiContext.Provider>
  );
}

export function useUi() {
  return useContext(UiContext);
}
